import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Trash2, Calculator } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { formatCurrency } from '../../lib/utils';
import api from '../../api/clients';
import { z } from 'zod';

const invoiceSchema = z.object({
  clienteNombre: z.string().min(2, 'El nombre del cliente es requerido'),
  clienteDocumento: z.string().optional(),
  clienteLocalidad: z.string().optional(),
  clienteDireccion: z.string().optional(),
  clienteProvincia: z.string().optional(),
  clienteCp: z.string().optional(),
  productos: z.array(z.object({
    nombre: z.string().min(1, 'El nombre del producto es requerido'),
    cantidad: z.number().min(1, 'La cantidad debe ser al menos 1'),
    precioUnitario: z.number().min(0, 'El precio no puede ser negativo'),
  })).min(1, 'Agrega al menos un producto'),
  costoEnvio: z.number().min(0).default(0),
  promocionPorcentaje: z.number().min(0).default(0),
  observaciones: z.string().optional(),
});

type InvoiceFormData = z.infer<typeof invoiceSchema>;

export function InvoiceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: factura, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.get(`/invoices/${id}`).then(res => res.data.data),
    enabled: isEditing,
  });

  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<InvoiceFormData>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      productos: [{ nombre: '', cantidad: 1, precioUnitario: 0 }],
      costoEnvio: 0,
      promocionPorcentaje: 0,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'productos',
  });

  const watchProductos = watch('productos');
  const watchCostoEnvio = watch('costoEnvio');
  const watchPromocion = watch('promocionPorcentaje');

  const calcularTotales = () => {
    const subtotal = watchProductos.reduce((sum, p) => {
      return sum + (p.cantidad || 0) * (p.precioUnitario || 0);
    }, 0);
    const iva = subtotal * 0.21;
    const descuento = subtotal * (watchPromocion || 0) / 100;
    const total = subtotal + iva + (watchCostoEnvio || 0) - descuento;
    return { subtotal, iva, descuento, total };
  };

  const totals = calcularTotales();

  useEffect(() => {
    if (factura && isEditing) {
      setValue('clienteNombre', factura.clienteNombre);
      setValue('clienteDocumento', factura.clienteDocumento || '');
      setValue('clienteLocalidad', factura.clienteLocalidad || '');
      setValue('clienteDireccion', factura.clienteDireccion || '');
      setValue('clienteProvincia', factura.clienteProvincia || '');
      setValue('clienteCp', factura.clienteCp || '');
      setValue('costoEnvio', Number(factura.costoEnvio || 0));
      setValue('promocionPorcentaje', Number(factura.promocionPorcentaje || 0));
      setValue('observaciones', factura.observaciones || '');
      
      const productos = JSON.parse(factura.productosJson || '[]');
      if (productos.length > 0) {
        setValue('productos', productos);
      }
    }
  }, [factura, isEditing, setValue]);

  const mutation = useMutation({
    mutationFn: (data: InvoiceFormData) => {
      const payload = {
        ...data,
        productosJson: JSON.stringify(data.productos),
      };
      const endpoint = isEditing ? `/invoices/${id}` : '/invoices';
      const method = isEditing ? 'PUT' : 'POST';
      return api({ method, url: endpoint, data: payload });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: () => {
      // error manejado en onSubmit
    },
  });

  const onSubmit = async (data: InvoiceFormData) => {
    try {
      await mutation.mutateAsync(data);
      toast.success(isEditing ? 'Factura actualizada exitosamente' : 'Comprobante creado exitosamente');
      navigate('/facturas');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al guardar la factura');
    }
  };

  if (isLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando factura...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/facturas')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Editar Factura Prepago' : 'Nueva Factura Prepago'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl shadow-sm p-6 max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Datos del Cliente</h3>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo *</label>
            <Input
              {...register('clienteNombre')}
              placeholder="Ej: Juan Pérez"
              error={errors.clienteNombre?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Documento</label>
            <Input
              {...register('clienteDocumento')}
              placeholder="Ej: 12345678"
              error={errors.clienteDocumento?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Localidad</label>
            <Input
              {...register('clienteLocalidad')}
              placeholder="Ej: San Andrés de Giles"
              error={errors.clienteLocalidad?.message}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
            <Input
              {...register('clienteDireccion')}
              placeholder="Ej: Av. Tecnología 123"
              error={errors.clienteDireccion?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
            <Input
              {...register('clienteProvincia')}
              placeholder="Ej: Buenos Aires"
              error={errors.clienteProvincia?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Código Postal</label>
            <Input
              {...register('clienteCp')}
              placeholder="Ej: 6720"
              error={errors.clienteCp?.message}
            />
          </div>

          <div className="md:col-span-2 border-t pt-4 mt-2">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Productos</h3>
          </div>

          {fields.map((field, index) => (
            <div key={field.id} className="md:col-span-2 grid grid-cols-1 md:grid-cols-12 gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="md:col-span-5">
                <label className="block text-xs font-medium text-gray-600 mb-1">Producto</label>
                <Input
                  {...register(`productos.${index}.nombre`)}
                  placeholder="Nombre del producto"
                  error={errors.productos?.[index]?.nombre?.message}
                  className="text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Cantidad</label>
                <Input
                  type="number"
                  {...register(`productos.${index}.cantidad`, { valueAsNumber: true })}
                  placeholder="1"
                  error={errors.productos?.[index]?.cantidad?.message}
                  className="text-sm"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Precio Unitario</label>
                <Input
                  type="number"
                  step="0.01"
                  {...register(`productos.${index}.precioUnitario`, { valueAsNumber: true })}
                  placeholder="0.00"
                  error={errors.productos?.[index]?.precioUnitario?.message}
                  className="text-sm"
                />
              </div>
              <div className="md:col-span-2 flex items-end justify-end gap-2">
                <div className="text-sm text-gray-500 pt-1">
                  Subtotal: {formatCurrency((watchProductos[index]?.cantidad || 0) * (watchProductos[index]?.precioUnitario || 0))}
                </div>
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}

          <div className="md:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => append({ nombre: '', cantidad: 1, precioUnitario: 0 })}
            >
              <Plus size={16} className="mr-2" />
              Agregar Producto
            </Button>
          </div>

          <div className="md:col-span-2 border-t pt-4 mt-2">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Condiciones</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Costo de Envío</label>
            <Input
              type="number"
              step="0.01"
              {...register('costoEnvio', { valueAsNumber: true })}
              placeholder="0.00"
              error={errors.costoEnvio?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Promoción (%)</label>
            <Input
              type="number"
              step="0.01"
              {...register('promocionPorcentaje', { valueAsNumber: true })}
              placeholder="0"
              error={errors.promocionPorcentaje?.message}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
            <textarea
              {...register('observaciones')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Observaciones adicionales..."
            />
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <div className="flex items-center gap-2 mb-3">
            <Calculator size={18} className="text-blue-600" />
            <h4 className="font-semibold text-blue-700">Resumen de la Factura</h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Subtotal</span>
              <p className="font-semibold text-gray-800">{formatCurrency(totals.subtotal)}</p>
            </div>
            <div>
              <span className="text-gray-500">IVA (21%)</span>
              <p className="font-semibold text-gray-800">{formatCurrency(totals.iva)}</p>
            </div>
            <div>
              <span className="text-gray-500">Descuento</span>
              <p className="font-semibold text-red-600">-{formatCurrency(totals.descuento)}</p>
            </div>
            <div>
              <span className="text-gray-500">Total</span>
              <p className="font-semibold text-blue-600 text-lg">{formatCurrency(totals.total)}</p>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6 pt-6 border-t">
          <Button type="submit" disabled={isSubmitting}>
            <Save size={18} />
            {isSubmitting ? 'Guardando...' : isEditing ? 'Actualizar Factura' : 'Crear Factura'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/facturas')}
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
}